import { DataTableDefinition } from "@/v2/types/Datatable";
import { ColumnDef, ColumnHelper } from "@tanstack/react-table";
import DefaultCell from "../cell/Default";
import CenterCell from "../cell/Center";
import HeadingCenterCell from "../cell/heading/Center";
import HeadingDefaultCell from "../cell/heading/Default";
import HeadingSmallCell from "../cell/heading/Small";
import MoneyCell from "../cell/Money";
import DateCell from "../cell/Date";
import IndexCell from "../cell/Index";
import RepresentativeCell from "../cell/heading/Representative";
import { ModuleKey } from "../../rbac/modules";
import ignoreFields from "@/lib/ignoreFields";
import { store } from "@/store/store";

function renderHeadingCell<T>(col: DataTableDefinition<T>) {
    if (col.headingCellType === "text-center") {
        return <HeadingCenterCell value={col.headingText} />;
    }

    if (col.headingCellType === "representative") {
        return <RepresentativeCell value={col.headingText} />;
    }

    if (col.headingCellType === "small-cell") {
        return <HeadingSmallCell value={col.headingText} />;
    }

    return <HeadingDefaultCell value={col.headingText} />;
}

function getModifiedValue<T>(
    col: DataTableDefinition<T>,
    value: any,
    index: number,
    row: T,
): any {
    if (!col.valueModifier) return value;
    try {
        return col.valueModifier({ value, index, row });
    } catch (error) {
        console.error(
            `Error in value modifier for column ${String(col.id)}:`,
            error,
            "Value:",
            value,
        );
        return value;
    }
}

function renderCell<T>(
    col: DataTableDefinition<T>,
    value: any,
    index: number,
    row: T,
) {
    if (col.id === "sl") {
        return <IndexCell index={index} />;
    }

    if (col.cell) {
        return col.cell({ value, index, row });
    }

    if (col.cellType === "text-center") {
        return <CenterCell value={value} minWidth={col.minWidth} />;
    }

    if (col.cellType === "money") {
        return <MoneyCell value={value} minWidth={col.minWidth} />;
    }

    if (col.cellType === "date") {
        return <DateCell value={value} minWidth={col.minWidth} />;
    }

    return <DefaultCell minWidth={col.minWidth ?? null} value={value} />;
}

export function buildColumns<T>(
    columnHelper: ColumnHelper<T>,
    columnDefs: DataTableDefinition<T>[],
    module?: ModuleKey,
): ColumnDef<T, any>[] {
    const user = store.getState().user.value;

    const filteredColumns = module
        ? columnDefs.filter((cell) => {
              return (
                  ignoreFields(user, {
                      module,
                      column: cell.id as string,
                  }) !== true
              );
          })
        : columnDefs;

    return filteredColumns.map((col) =>
        columnHelper.accessor(col.id as any, {
            header: () => renderHeadingCell(col),
            cell: (info) => {
                const value = info.getValue();
                const index = info.row.index;
                const row = info.row.original as T;
                const modifiedValue = getModifiedValue(col, value, index, row);

                try {
                    return renderCell(col, modifiedValue, index, row);
                } catch (error) {
                    console.error(
                        `Error rendering cell for column ${String(col.id)}:`,
                        error,
                        "Value:",
                        modifiedValue,
                    );
                    return (
                        <DefaultCell
                            minWidth={col.minWidth ?? null}
                            value={null}
                        />
                    );
                }
            },
        }),
    );
}
